import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { PhoneField } from '../components/PhoneField';
import { authService } from '../services/api';
import { env } from '../env';

const ForgotPassword: React.FC = () => {
  const smsDisabled = env.REACT_APP_SMS_DISABLED === 'true';
  const [channel, setChannel] = useState<'email' | 'sms'>('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setStatus(null);
    setLoading(true);
    try {
      if (channel === 'email') {
        const trimmedEmail = email.trim().toLowerCase();
        if (!trimmedEmail) {
          setError('Enter your email address.');
          setLoading(false);
          return;
        }
        const response = await authService.requestPasswordReset({ email: trimmedEmail, channel: 'email' });
        setStatus(response.message);
      } else {
        if (smsDisabled) {
          setError('Text message resets are temporarily unavailable.');
          setLoading(false);
          return;
        }
        if (!phone || !isValidPhoneNumber(phone)) {
          setError('Enter a valid phone number with country code.');
          setLoading(false);
          return;
        }
        const response = await authService.requestPasswordReset({ phone, channel: 'sms' });
        setStatus(response.message);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Unable to send reset link.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 px-6 py-16 text-white">
      <div className="max-w-lg w-full bg-white/10 border border-white/20 rounded-3xl p-8 backdrop-blur">
        <h1 className="text-2xl font-semibold mb-2">Forgot your password?</h1>
        <p className="text-sm text-white/70 mb-6">
          Choose where to send your secure reset link.
        </p>
        <form className="space-y-4" onSubmit={handleSubmit}>
          {status && <div className="text-sm text-emerald-200">{status}</div>}
          {error && <div className="text-sm text-red-300">{error}</div>}
          <div>
            <div className="flex gap-2 mb-3">
              <button
                type="button"
                onClick={() => setChannel('email')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold border ${
                  channel === 'email'
                    ? 'bg-emerald-600 border-emerald-500 text-white'
                    : 'bg-white/5 border-white/20 text-white/70 hover:bg-white/10'
                }`}
              >
                Email
              </button>
              {!smsDisabled && (
                <button
                  type="button"
                  onClick={() => setChannel('sms')}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold border ${
                    channel === 'sms'
                      ? 'bg-emerald-600 border-emerald-500 text-white'
                      : 'bg-white/5 border-white/20 text-white/70 hover:bg-white/10'
                  }`}
                >
                  Text message
                </button>
              )}
            </div>
            {channel === 'email' ? (
              <>
                <label className="block text-xs uppercase tracking-wide text-white/60 mb-1">
                  Email address
                </label>
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  placeholder="you@example.com"
                  type="email"
                  autoComplete="email"
                />
              </>
            ) : smsDisabled ? null : (
              <PhoneField
                label={<span className="text-xs uppercase tracking-wide text-white/60">Phone number</span>}
                value={phone || undefined}
                onChange={(value) => setPhone(value || '')}
                inputClassName="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-emerald-400"
                containerClassName="text-white"
                labelClassName="text-white/60"
                hideValidationMessage
              />
            )}
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 font-semibold disabled:opacity-60"
          >
            {loading ? 'Sending…' : 'Send reset link'}
          </button>
        </form>
        <div className="mt-6 text-sm text-white/70">
          Remembered your password?{' '}
          <Link to="/login" className="text-emerald-200 hover:text-emerald-100">
            Back to login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
